import { useState, useEffect, useRef } from 'react';
import { UseQueryResult } from '@tanstack/react-query';

/**
 * Default minimum loading time in milliseconds to ensure users see loading state feedback.
 * This prevents jarring instant updates when data is cached or API responds very quickly.
 */
export const DEFAULT_MINIMUM_LOADING_TIME_MS = 250;

/**
 * Custom hook that wraps a TanStack Query result and enforces a minimum loading duration.
 * Ensures users see loading states for at least the specified duration, even with cache hits.
 *
 * @param queryResult - The TanStack Query result to wrap
 * @param minimumDuration - Minimum loading time in milliseconds (default: 250ms)
 * @returns Modified query result with enforced minimum loading time
 */
export function useMinimumLoadingTime<TData = unknown, TError = Error>(
	queryResult: UseQueryResult<TData, TError>,
	minimumDuration: number = DEFAULT_MINIMUM_LOADING_TIME_MS
): UseQueryResult<TData, TError> {
	const [isDelayedLoading, setIsDelayedLoading] = useState(false);
	const fetchStartTimeRef = useRef<number | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		// Clear any existing timeout to prevent memory leaks
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		// When query starts fetching (including cache updates)
		if (queryResult.isFetching && !fetchStartTimeRef.current) {
			fetchStartTimeRef.current = Date.now();
			setIsDelayedLoading(true);
		}

		// When query finishes fetching
		if (!queryResult.isFetching && fetchStartTimeRef.current) {
			const elapsedTime = Date.now() - fetchStartTimeRef.current;
			const remainingTime = minimumDuration - elapsedTime;

			if (remainingTime > 0) {
				// Keep loading state for remaining time
				timeoutRef.current = setTimeout(() => {
					setIsDelayedLoading(false);
					fetchStartTimeRef.current = null;
				}, remainingTime);
			}
			else {
				// Minimum time already elapsed
				setIsDelayedLoading(false);
				fetchStartTimeRef.current = null;
			}
		}

		// Cleanup timeout on unmount or when dependencies change
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [queryResult.isFetching, minimumDuration]);

	// Return modified query with enforced minimum loading time
	return {
		...queryResult,
		isLoading: queryResult.isLoading || isDelayedLoading,
		isFetching: queryResult.isFetching || isDelayedLoading
	} as UseQueryResult<TData, TError>;
}
